import {
  Truck, Calendar, User, CheckCircle 
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import type { Delivery } from '../../types';
import {
  DELIVERY_STATUS_LABELS, DELIVERY_STATUS_COLORS 
} from '../../types';

interface DeliveryCardProps {readonly delivery: Delivery;}

export function DeliveryCard({ delivery }: DeliveryCardProps) {
  const statusColor = DELIVERY_STATUS_COLORS[delivery.status];
  const progress = delivery.totalLines > 0 ? (delivery.receivedLines / delivery.totalLines) * 100 : 0;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="text-base font-medium">{delivery.poNumber}</CardTitle>
          </div>
          <span className={`rounded-full px-2 py-1 text-xs font-medium ${statusColor}`}>
            {DELIVERY_STATUS_LABELS[delivery.status]}
          </span>
        </div>
        <p className="text-sm text-muted-foreground">{delivery.supplierName}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="h-4 w-4" />
          <span>Scheduled: {new Date(delivery.scheduledDate).toLocaleDateString()}</span>
        </div>

        {delivery.actualArrivalDate && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <span>Arrived: {new Date(delivery.actualArrivalDate).toLocaleDateString()}</span>
          </div>
        )}

        {delivery.receivedByName && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <User className="h-4 w-4" />
            <span>Received by: {delivery.receivedByName}</span>
          </div>
        )}

        <div className="space-y-1">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-medium">{delivery.receivedLines}/{delivery.totalLines} lines</span>
          </div>
          <div className="h-2 w-full rounded-full bg-muted">
            <div className="h-2 rounded-full bg-primary transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div className="pt-2 border-t">
          <p className="text-xs text-muted-foreground mb-2">Line Items:</p>
          <div className="space-y-1">
            {delivery.lines.slice(0, 3).map((line) => (
              <div key={line.lineId} className="flex items-center justify-between text-sm">
                <span className="truncate flex-1">{line.itemName}</span>
                <span className="text-muted-foreground ml-2">
                  {line.receivedQuantity ?? 0}/{line.orderedQuantity} {line.unit}
                </span>
              </div>
            ))}
            {delivery.lines.length > 3 && (
              <p className="text-xs text-muted-foreground">+{delivery.lines.length - 3} more items</p>
            )}
          </div>
        </div>

        {delivery.notes && (
          <p className="text-sm text-muted-foreground italic border-l-2 border-muted pl-2">
            {delivery.notes}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
